import { Badge } from '@/components/atoms/Badge'
import { AlertInbox } from '@/components/organisms/AlertInbox'
import { MissedEmergencyList } from '@/components/organisms/SosEmergencyModal'
import { useMissedEmergencies } from '@/hooks/useMissedEmergencies'
import { useRobotAlerts } from '@/hooks/useRobotAlerts'
import { getLiveChannelUrl } from '@/services/auth/config'
import { Bell } from 'lucide-react'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'

/**
 * Header bell pill with an open-alert count badge. Click toggles a right-side
 * drawer hosting the shared <AlertInbox/>. Self-gates to null when the live
 * channel is unavailable (getLiveChannelUrl() === null) — on-prem / no broker.
 *
 * Missed emergencies are listed FIRST in the drawer, on every page, with their own amber count on
 * the bell: a critical alarm nobody answered is the one thing in here that is still waiting for
 * someone, and the dashboard pill only exists on a dashboard that shows the device.
 */
export function RobotAlertsBell() {
  const { t } = useTranslation('console')
  const { count } = useRobotAlerts()
  const missed = useMissedEmergencies()
  const [isOpen, setIsOpen] = useState(false)

  // No broker → no live alerts → don't show the bell.
  let hasLiveChannel = false

  try {
    hasLiveChannel = getLiveChannelUrl() !== null
  } catch {
    hasLiveChannel = false
  }

  if (!hasLiveChannel) return null

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(true)}
        aria-label={t('alerts.bell.open', 'Open alerts')}
        className="relative flex items-center rounded-lg p-2 text-white/80 transition-colors hover:bg-white/10"
      >
        <Bell className="h-5 w-5" />
        {count > 0 && (
          <span data-testid="robot-alerts-count" className="absolute -right-1 -top-1">
            <Badge variant="error" size="sm">
              {count}
            </Badge>
          </span>
        )}
        {missed.length > 0 && (
          <span
            data-testid="robot-alerts-missed-count"
            title={t('alerts.sos.missedCount', '{{count}} missed emergencies', { count: missed.length })}
            className="absolute -bottom-1 -left-1 rounded-full bg-amber-500 px-1 text-[10px] font-bold leading-4 text-white"
          >
            {missed.length}
          </span>
        )}
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div
            className="fixed inset-0 z-40 bg-black/30 dark:bg-black/50"
            onClick={() => setIsOpen(false)}
            aria-hidden="true"
          />

          {/* Drawer */}
          <aside
            role="dialog"
            aria-label={t('alerts.drawer.title', 'Robot alerts')}
            className="fixed inset-y-0 right-0 z-50 flex w-full max-w-md flex-col bg-white shadow-xl dark:bg-gray-900"
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-5 py-4 dark:border-gray-700">
              <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
                {t('alerts.drawer.title', 'Robot alerts')}
              </h2>
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="rounded p-1.5 text-gray-400 hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-gray-800 dark:hover:text-gray-200"
                aria-label={t('alerts.drawer.close', 'Close')}
              >
                <svg className="h-5 w-5" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
                  <path d="M6.28 5.22a.75.75 0 00-1.06 1.06L8.94 10l-3.72 3.72a.75.75 0 101.06 1.06L10 11.06l3.72 3.72a.75.75 0 101.06-1.06L11.06 10l3.72-3.72a.75.75 0 00-1.06-1.06L10 8.94 6.28 5.22z" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-auto">
              {missed.length > 0 && (
                <section className="border-b-2 border-amber-500" data-testid="robot-alerts-missed">
                  <h3 className="bg-amber-50 px-5 py-2 text-xs font-bold uppercase tracking-wide text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
                    {t('alerts.sos.missedCount', '{{count}} missed emergencies', { count: missed.length })}
                  </h3>
                  <MissedEmergencyList items={missed} />
                </section>
              )}
              <AlertInbox />
            </div>
          </aside>
        </>
      )}
    </>
  )
}
